import React, { FormEventHandler, Fragment } from 'react';
import { connect } from 'dva';
import { Heading } from '@mos-cat/typography';
import { Dispatch } from 'redux';
import Select from '@mos-front-components/public/es/components/MosSelect';
import { t, Trans } from '@lingui/macro';

import { SECOND_PARENT_STATUS } from '../../../constants/secondParentStatus';
import Paternity from './Paternity';
import SecondParentDead from './SecondParentDead';
import SecondParentDivorced from './SecondParentDivorced';
import { getSecondParentsWithSameStatus } from '../../../models/children';
import renderSecondParentSuggestion, {
  ADD_INFO,
  SecondParentWithSameStatus,
} from './helpers/renderSecondParentSuggestion';
import {
  getActiveChildSecondParentStatusValue,
  getSelectedParent,
  getShowSecondParentContent,
} from '../../../selectors/child';
import { createStructuredSelector } from 'reselect';
import { Field } from '../../../declarations/widget-ums';
import { i18n } from '../../../helpers/i18n';

const CONTENT = {
  [SECOND_PARENT_STATUS.single]: null,
  [SECOND_PARENT_STATUS.paternity]: <Paternity />,
  [SECOND_PARENT_STATUS.widowed]: <SecondParentDead />,
  [SECOND_PARENT_STATUS.divorced]: <SecondParentDivorced />,
};

interface Props {
  status: string;
  selectedParent: Field<string>;
  id: string;
  showContent: boolean;
  secondParentsWithSameStatus: SecondParentWithSameStatus[];
  onSelect: FormEventHandler;

  toggleContentVisibility(payload: boolean): void;
}

function SecondParentInfo({
  secondParentsWithSameStatus,
  status,
  selectedParent,
  showContent,
  toggleContentVisibility,
  onSelect,
}: Props): JSX.Element | null {
  if (!status) return null;

  const handleSelect = (event) => {
    if (event.value === ADD_INFO) {
      toggleContentVisibility(true);
      return onSelect(event);
    }
    toggleContentVisibility(false);
    onSelect(event);
  };

  const { value, error } = selectedParent;

  const showSelect = () => {
    const placeholder = value.length
      ? i18n._(t`Второй родитель`)
      : i18n._(t`Укажите второго родителя`);

    const suggestions = [
      ...secondParentsWithSameStatus,
      {
        value: ADD_INFO,
        text: i18n._(t`Добавить сведения о втором родителе`),
      },
    ];

    return (
      <div className="SecondParentInfoSelectWrapper">
        <Heading level={3}>
          <Trans>Сведения о втором родителе ребёнка</Trans>
        </Heading>
        <Select
          key={status}
          className="SecondParentInfoSelect ym-hide-content"
          name="selectedParent"
          placeholder={placeholder}
          suggestions={suggestions}
          value={value}
          onChange={handleSelect}
          renderSuggestion={renderSecondParentSuggestion}
          hasError={error}
          dropListView
        />
        {showContent && CONTENT[status]}
        {value && value !== ADD_INFO && (
          <button
            type="button"
            className="mos-link"
            onClick={() => toggleContentVisibility(!showContent)}
          >
            <Trans
              id={showContent ? t`Скрыть сведения` : t`Показать сведения`}
            />
          </button>
        )}
      </div>
    );
  };

  const select = secondParentsWithSameStatus.length ? showSelect() : null;

  return (
    <Fragment>
      {select}
      {!secondParentsWithSameStatus.length && CONTENT[status]}
    </Fragment>
  );
}

const mapStateToProps = createStructuredSelector<any, any>({
  secondParentsWithSameStatus: getSecondParentsWithSameStatus,
  status: getActiveChildSecondParentStatusValue,
  selectedParent: getSelectedParent,
  showContent: getShowSecondParentContent,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  onSelect(payload) {
    dispatch({
      type: 'child/secondParent/select',
      payload,
    });
  },
  toggleContentVisibility(payload) {
    dispatch({
      type: 'child/secondParentContent/show',
      payload,
    });
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(SecondParentInfo);
